/**
 * Admin Payment Error Utilities
 * Provides consistent, user-friendly error messages across the admin application
 */

import { APIError } from '@/lib/api';

export interface PaymentError {
  title: string;
  message: string;
  action?: string;
  severity: 'error' | 'warning' | 'info';
}

/** The parts of a failed request this file reads, whichever shape the failure arrived in. */
interface ErrorShape {
  status?: number;
  code?: string;
  message?: string;
  details?: unknown;
}

/** A StandardResponse error body, as it looks on a request that did not go through the interceptor. */
interface RawErrorBody {
  error?: { code?: string; message?: string; details?: unknown } | string;
  code?: string;
  message?: string;
  details?: unknown;
}

/**
 * Reads status, code, message and details off a failure.
 *
 * `lib/api.ts` rejects every StandardResponse error body — and timeouts and network failures — as
 * an `APIError`, which has no `.response`. This file used to look only for axios's `.response`, so
 * against a real request nothing ever matched and every refusal fell through to the generic
 * "Operation Failed". The raw axios shape is still read, for a request that did not go through the
 * interceptor.
 */
function readErrorShape(error: unknown): ErrorShape | null {
  if (error instanceof APIError) {
    return {
      status: error.statusCode,
      code: error.code,
      message: error.message,
      details: error.details,
    };
  }

  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { status?: number; data?: RawErrorBody } }).response;
    const data = response?.data;
    const nested = typeof data?.error === 'object' ? data.error : undefined;
    return {
      status: response?.status,
      code: nested?.code || data?.code || (typeof data?.error === 'string' ? data.error : undefined),
      message: nested?.message || data?.message,
      details: data?.details ?? nested?.details,
    };
  }

  return null;
}

/**
 * Maps backend error codes/messages to user-friendly PaymentError objects
 */
export function parsePaymentError(error: unknown): PaymentError {
  const shape = readErrorShape(error);

  if (shape) {
    const status = shape.status ?? 0;
    const errorCode = shape.code;
    const errorMessage = shape.message;

    // Map specific error codes
    switch (errorCode) {
      case 'PAYMENT_NOT_FOUND':
        return {
          title: 'Payment Not Found',
          message: "The payment you're looking for doesn't exist or has been removed.",
          action: 'Refresh the payment queue and try again.',
          severity: 'error',
        };

      case 'PAYMENT_ALREADY_VERIFIED':
        return {
          title: 'Payment Already Verified',
          message: 'This payment has already been verified by another administrator.',
          action: 'No further action needed. The payment queue will be updated automatically.',
          severity: 'info',
        };

      case 'PAYMENT_ALREADY_REJECTED':
        return {
          title: 'Payment Already Rejected',
          message: 'This payment has already been rejected.',
          action: 'No further action can be taken.',
          severity: 'info',
        };

      // The verify endpoint now returns the handler's own code instead of flattening everything
      // to VERIFICATION_FAILED, so the two refusals an operator can actually act on get their own
      // guidance rather than a generic "try again".
      case 'AMOUNT_MISMATCH':
        return {
          title: 'Amount Does Not Match',
          message:
            errorMessage ||
            'The amount you entered does not match the amount this payment was requested for.',
          action:
            'Do not verify. Reject this payment and ask the customer to make a new deposit for the amount they actually transferred.',
          severity: 'error',
        };

      case 'GATEWAY_OVERRIDE_REASON_REQUIRED':
        return {
          title: 'Reason Required',
          message:
            errorMessage ||
            'This bill was issued by a payment gateway that has not confirmed it.',
          action:
            'Explain in the notes how you confirmed the money arrived, then verify again.',
          severity: 'warning',
        };

      case 'ORDER_ALREADY_SETTLED':
        return {
          title: 'Order Already Paid',
          message:
            errorMessage ||
            'The order behind this transfer is no longer awaiting payment, so approving it would charge the customer a second time.',
          action:
            'Do not verify. The customer already settled this order another way — return the transfer to them, then reject this payment.',
          severity: 'error',
        };

      // A renewal payment whose VM cannot be renewed any more (the backend's RenewalBlock, 409).
      // Nothing was recorded and the payment is untouched — the fault is the VM, not the transfer,
      // so the operator is told what happened to the machine rather than "try again".
      case 'RESOURCE_BEING_DELETED':
        return {
          title: 'VM Is Being Deleted',
          message:
            errorMessage ||
            'The VM this renewal is for is being deleted, so it cannot be renewed.',
          action:
            'Nothing was recorded. Wait for the deletion to finish: if it fails the VM is back and you can verify again; if it completes, return the transfer to the customer and reject this payment.',
          severity: 'warning',
        };

      case 'RESOURCE_REINSTALLING':
        return {
          title: 'VM Is Being Reinstalled',
          message:
            errorMessage ||
            'The VM this renewal is for is being reinstalled, so it cannot be renewed right now.',
          action:
            'Nothing was recorded. Verify this payment again once the reinstall has finished.',
          severity: 'warning',
        };

      case 'RESOURCE_NOT_FOUND':
        return {
          title: 'VM No Longer Exists',
          message:
            errorMessage ||
            'The VM this renewal is for has been deleted, so the renewal cannot be applied.',
          action:
            'Do not verify. Return the transfer to the customer, then reject this payment.',
          severity: 'error',
        };

      // Verify, reject and refund all answer 409 CONFLICT when the payment moved
      // between the operator reading the row and acting on it — including a payment another
      // operator (or the customer) already decided. Nothing was written, so the honest instruction is
      // "look again", not "try again" — whatever the row says now may be somebody else's
      // decision. The mutation's own `onSettled` reloads the queue, so the reload is not manual.
      case 'CONFLICT':
        return {
          title: 'Payment Changed',
          message:
            errorMessage ||
            'This payment changed while you were reviewing it. Nothing was recorded.',
          action:
            'The list has been refreshed. Check the payment’s current status before deciding again.',
          severity: 'warning',
        };

      case 'VERIFICATION_FAILED':
        return {
          title: 'Verification Failed',
          message: errorMessage || 'Unable to verify this payment at this time.',
          action: 'Check the payment details and try again. Contact technical support if the issue persists.',
          severity: 'error',
        };

      case 'REJECTION_FAILED':
        return {
          title: 'Rejection Failed',
          message: errorMessage || 'Unable to reject this payment at this time.',
          action: 'Try again or contact technical support if the issue persists.',
          severity: 'error',
        };

      case 'BULK_VERIFICATION_FAILED':
        return {
          title: 'Bulk Verification Failed',
          message: errorMessage || 'Unable to verify some payments in this batch.',
          action: 'Review the failed payments and try them individually.',
          severity: 'warning',
        };

      case 'PAYMENT_ALREADY_REFUNDED':
        return {
          title: 'Already Refunded',
          message: 'This payment has already been refunded.',
          action: 'No further action can be taken.',
          severity: 'info',
        };

      case 'REFUND_FAILED':
        return {
          title: 'Refund Failed',
          message: errorMessage || 'Unable to process the refund at this time.',
          action: 'Check the payment status and account balance, then try again.',
          severity: 'error',
        };

      case 'INSUFFICIENT_BALANCE_FOR_REFUND':
        return {
          title: 'Insufficient Balance',
          message: "The tenant's account balance is too low to process this refund.",
          action: 'Contact the tenant to arrange an alternative refund method.',
          severity: 'error',
        };

      case 'INVALID_PAYMENT_STATUS':
        return {
          title: 'Invalid Payment Status',
          message: errorMessage || 'This operation cannot be performed on payments with this status.',
          action: 'Verify the payment status and try the appropriate action.',
          severity: 'error',
        };

      case 'MISSING_PAYMENT_PROOF':
        return {
          title: 'Missing Payment Proof',
          message: 'This payment does not have an uploaded proof document.',
          action: 'Request the tenant to upload payment proof before verification.',
          severity: 'warning',
        };

      case 'TENANT_NOT_FOUND':
        return {
          title: 'Tenant Not Found',
          message: 'The tenant associated with this payment no longer exists.',
          action: 'This payment may require manual review. Contact technical support.',
          severity: 'error',
        };

      case 'BALANCE_UPDATE_FAILED':
        return {
          title: 'Balance Update Failed',
          message: 'Payment was verified but the account balance could not be updated.',
          action: 'This requires immediate attention. Contact technical support to manually credit the account.',
          severity: 'error',
        };

      case 'VALIDATION_ERROR': {
        // Extract validation errors from details
        const validationDetails = shape.details as Array<{ field: string; reason: string }> | undefined;
        if (validationDetails && Array.isArray(validationDetails) && validationDetails.length > 0) {
          const validationMessages = validationDetails.map((err) => `${err.field}: ${err.reason}`).join('\n');
          return {
            title: 'Validation Error',
            message: errorMessage || 'Please check your input and try again.',
            action: validationMessages,
            severity: 'error',
          };
        }
        return {
          title: 'Validation Error',
          message: errorMessage || 'Please check your input and try again.',
          action: 'Review the form fields and ensure all required information is provided correctly.',
          severity: 'error',
        };
      }

      default:
        // HTTP status code fallbacks
        if (status === 401) {
          return {
            title: 'Session Expired',
            message: 'Your admin session has expired.',
            action: 'Please log in again to continue.',
            severity: 'warning',
          };
        }
        if (status === 403) {
          return {
            title: 'Access Denied',
            message: "You don't have permission to perform this admin action.",
            action: 'Contact your administrator if you believe this is an error.',
            severity: 'error',
          };
        }
        if (status === 404) {
          return {
            title: 'Resource Not Found',
            message: errorMessage || 'The requested resource was not found.',
            action: 'Refresh the page and try again.',
            severity: 'error',
          };
        }
        if (status === 409) {
          return {
            title: 'Conflict',
            message: errorMessage || 'This action conflicts with the current state.',
            action: 'Refresh the data and try again.',
            severity: 'warning',
          };
        }
        if (status === 422) {
          // Unprocessable Entity - Validation errors
          const validationDetails = shape.details as Array<{ field: string; reason: string }> | undefined;
          if (validationDetails && Array.isArray(validationDetails) && validationDetails.length > 0) {
            const validationMessages = validationDetails.map((err) => `${err.field}: ${err.reason}`).join('\n');
            return {
              title: 'Validation Error',
              message: errorMessage || 'Please check your input and try again.',
              action: validationMessages,
              severity: 'error',
            };
          }
          return {
            title: 'Validation Error',
            message: errorMessage || 'The provided data is invalid.',
            action: 'Please review the form and correct any errors.',
            severity: 'error',
          };
        }
        if (status >= 500) {
          return {
            title: 'Server Error',
            // The server's own sentence when it wrote one ("Failed to verify payment."), which names
            // the operation that failed; the generic line only when it did not.
            message: errorMessage || 'The admin server is experiencing issues.',
            action: 'Please try again in a few moments. Contact technical support if the problem persists.',
            severity: 'error',
          };
        }
    }
  }

  // Network errors — the interceptor's NETWORK_ERROR, or an adapter that only says so in its message.
  if (shape?.code === 'NETWORK_ERROR' || (error instanceof Error && /network/i.test(error.message))) {
    return {
      title: 'Network Error',
      message: 'Unable to connect to the admin server.',
      action: 'Check your internet connection and try again.',
      severity: 'error',
    };
  }

  // Generic fallback
  return {
    title: 'Operation Failed',
    message: error instanceof Error ? error.message : 'An unexpected error occurred.',
    action: 'Please try again or contact technical support if the problem persists.',
    severity: 'error',
  };
}

/**
 * Shows error toast with parsed error information
 */
export function showPaymentError(error: unknown, toast: any) {
  const parsedError = parsePaymentError(error);

  const description = parsedError.action
    ? `${parsedError.message}\n\n${parsedError.action}`
    : parsedError.message;

  if (parsedError.severity === 'warning') {
    toast.warning(parsedError.title, { description });
  } else if (parsedError.severity === 'info') {
    toast.info(parsedError.title, { description });
  } else {
    toast.error(parsedError.title, { description });
  }
}
